import {
  useCallback, useEffect, useRef, useState,
} from 'react'
import { gql, useLazyQuery } from '@apollo/client'
import { useSelector } from 'react-redux'
import { selectAuth } from '../../../services/auth/selectors'
import { selectPrimaryLanguage } from '../../../services/user/selectors'
import { EN } from '../../../services/languages/constants'
import { useVideoState } from '../../OracleCommons/useVideoState'
import type { ConceptMoment } from './useConceptMoments'
import type { VideoPromptSuggestion } from './useVideoPromptSuggestions'

// Inline gql document — move to queries.graphql and use generated hook
// once the backend deploys the videoConceptMomentPrompts endpoint.
const VIDEO_CONCEPT_MOMENT_PROMPTS = gql`
  query VideoConceptMomentPrompts($input: VideoConceptMomentPromptsInput!) {
    videoConceptMomentPrompts(input: $input) {
      prompts {
        prompt
        focus
        rationale
      }
      conceptLabel
      generatedAt
    }
  }
`

export interface UseConceptMomentPromptsResult {
  suggestions: VideoPromptSuggestion[] | null
  refetch: (excludePrompts?: string[]) => void
  /** Clear moment suggestions so the parent falls back to generic prompts */
  clear: () => void
}

/**
 * Builds the GraphQL input object for a moment prompt request.
 */
function buildMomentInput (
  moment: ConceptMoment,
  contentId: string,
  featureMediaId: string,
  language: string,
  excludePrompts?: Set<string>,
): Record<string, unknown> {
  const input: Record<string, unknown> = {
    contentId,
    featureMediaId,
    startSeconds: moment.startSeconds,
    endSeconds: moment.endSeconds,
    conceptLabel: moment.conceptLabel,
    language,
  }
  if (excludePrompts && excludePrompts.size > 0) {
    input.excludePrompts = Array.from(excludePrompts)
  }
  return input
}

/**
 * Fetches moment-scoped prompt suggestions when the user enters a
 * dense concept moment.
 *
 * Display rules:
 * - Suggestions persist after leaving a moment window (no jarring
 *   fallback to generic prompts mid-video).
 * - Replaced when a new moment is entered, the video changes, or
 *   the parent calls clear() (e.g. user clicks a prompt outside
 *   a moment, triggering a generic refetch instead).
 *
 * Stale-while-revalidate: during a refetch the current suggestions
 * remain visible until the new response arrives. This is achieved
 * by owning prompt state locally (useState) rather than reading
 * Apollo's data directly, which avoids both cache leaks and
 * loading-state flicker.
 *
 * Previously returned prompts are accumulated and sent as
 * excludePrompts so the backend generates fresh suggestions.
 */
export function useConceptMomentPrompts (
  activeMoment: ConceptMoment | null,
): UseConceptMomentPromptsResult {
  const auth = useSelector(selectAuth)
  const jwt = auth.get('jwt')
  const language = useSelector(selectPrimaryLanguage) || EN
  const { contentId, featureMediaId } = useVideoState()

  const [fetchPrompts, { data }] = useLazyQuery(VIDEO_CONCEPT_MOMENT_PROMPTS)

  // ── Local prompt state (decoupled from Apollo cache) ──────────────
  // Starts null. Only populated by our explicit fetch responses.
  // Survives loading states (stale-while-revalidate).
  const [prompts, setPrompts] = useState<VideoPromptSuggestion[] | null>(null)

  // Dedup: skip fetch if we already fetched for this exact moment
  const lastFetchedStartRef = useRef<number | null>(null)

  // Accumulate every prompt the API has returned for excludePrompts
  const shownPromptsRef = useRef<Set<string>>(new Set())

  // ── Sync Apollo response → local state ────────────────────────────
  useEffect(() => {
    const incoming = data?.videoConceptMomentPrompts?.prompts
    if (incoming?.length) {
      setPrompts(incoming)
      incoming.forEach((p: VideoPromptSuggestion) => {
        shownPromptsRef.current.add(p.prompt)
      })
    }
  }, [data])

  // ── Reset on video change ─────────────────────────────────────────
  useEffect(() => {
    setPrompts(null)
    shownPromptsRef.current = new Set()
    lastFetchedStartRef.current = null
  }, [contentId, featureMediaId])

  // ── Fetch on moment entry ─────────────────────────────────────────
  useEffect(() => {
    if (!activeMoment || !contentId || !featureMediaId || !jwt) {
      lastFetchedStartRef.current = null
      return
    }

    if (lastFetchedStartRef.current === activeMoment.startSeconds) return
    lastFetchedStartRef.current = activeMoment.startSeconds

    const exclude = shownPromptsRef.current
    const input = buildMomentInput(activeMoment, contentId, featureMediaId, language, exclude)
    fetchPrompts({
      variables: { input },
      context: { jwt },
      fetchPolicy: 'network-only',
    })
  }, [activeMoment, contentId, featureMediaId, jwt, language, fetchPrompts])

  // ── Refetch (click while in moment) ───────────────────────────────
  const refetch = useCallback((excludePrompts?: string[]) => {
    if (!activeMoment || !contentId || !featureMediaId || !jwt) return

    const allExclusions = new Set(shownPromptsRef.current)
    if (excludePrompts) {
      excludePrompts.forEach((p) => allExclusions.add(p))
    }

    const input = buildMomentInput(activeMoment, contentId, featureMediaId, language, allExclusions)
    fetchPrompts({
      variables: { input },
      context: { jwt },
      fetchPolicy: 'network-only',
    })
  }, [activeMoment, contentId, featureMediaId, jwt, language, fetchPrompts])

  // ── Clear (click outside moment → fall back to generic) ───────────
  const clear = useCallback(() => { setPrompts(null) }, [])

  return { suggestions: prompts, refetch, clear }
}
